"use client";

import Link from "next/link";
import type { Route } from "next";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { Briefcase, MapPin, Search, Users } from "lucide-react";

import { normalizeJobRole, normalizeJobType, normalizePublicJobs, publicJobPath } from "@/lib/public-jobs";
import type { Job } from "@/lib/public-jobs";
export type { Job } from "@/lib/public-jobs";

export type Talent = {
  id: number;
  name: string;
  role: string;
  location: string;
  experience: string;
  availability: string;
  bio?: string;
  skills?: string;
  photo_url?: string;
};

const SEEKER_INTENT_KEY = "cybo_seeker_intent";

function openSeekerFlow(action: "apply" | "save" | "contact", id?: number) {
  const params = new URLSearchParams();
  params.set("action", action);
  if (id) {
    params.set("ref", String(id));
  }
  try {
    window.localStorage.setItem(
      SEEKER_INTENT_KEY,
      JSON.stringify({ action, ref: id || null, createdAt: Date.now() })
    );
  } catch {
    // Continue to login even when local storage is unavailable.
  }
  window.location.assign(`/public/seeker-login?${params.toString()}`);
}

export default function JobsClient({ initialJobs = [], initialTalents = [], initialError = false }: { initialJobs?: Job[]; initialTalents?: Talent[]; initialError?: boolean }) {
  const [typeParam, setTypeParam] = useState("");

  const [jobs, setJobs] = useState<Job[]>(initialJobs);
  const [talents, setTalents] = useState<Talent[]>(initialTalents);
  const [retryVersion, setRetryVersion] = useState(0);
  const [loadError, setLoadError] = useState(initialError ? "Job Portal could not be loaded. Please retry." : "");
  const [tab, setTab] = useState<"jobs" | "talents">("jobs");
  const [term, setTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [employmentFilter, setEmploymentFilter] = useState("");

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      setTypeParam(params.get("role") || params.get("type") || "");
      if (params.get("employment")) setEmploymentFilter(normalizeJobType(params.get("employment") || ""));
    } catch {
      setTypeParam("");
    }
  }, []);

  useEffect(() => {
    const role = normalizeJobRole(typeParam);
    setRoleFilter(role);
  }, [typeParam]);

  useEffect(() => {
    if (!initialError) return;
    let active = true;
    async function load() {
      try {
        const res = await fetch("/api/v1/public/talent-network", { cache: "no-store" });
        if (!res.ok) throw new Error("Could not load Job Portal.");
        const data = (await res.json()) as {
          success?: boolean;
          jobs?: Array<{
              id?: number;
              title?: string;
              company?: string;
              location?: string;
              type?: string;
              description?: string;
              requirements?: string;
              application_method?: string;
              application_url?: string;
            }>;
          talents?: Array<{ id: number; name: string; headline?: string; location?: string; experience_years?: number; availability?: string; bio?: string; skills?: string; photo_url?: string }>;
        };

        if (data.success !== true || !Array.isArray(data.jobs)) throw new Error("Invalid job feed");
        if (!active) return;
        setJobs(normalizePublicJobs(data.jobs));
        setTalents((data.talents || []).map((person) => ({ id: person.id, name: person.name, role: person.headline || "Hospitality professional", location: person.location || "Location not shared", experience: `${Number(person.experience_years || 0)} year(s)`, availability: person.availability || "Open to opportunities", bio: person.bio, skills: person.skills, photo_url: person.photo_url })));
        setLoadError("");
      } catch {
        if (active) { setJobs([]); setTalents([]); setLoadError("Job Portal could not be loaded. Please retry."); }
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [initialError, retryVersion]);

  const locations = useMemo(() => {
    const vals = new Set<string>();
    jobs.forEach((j) => vals.add(j.location));
    talents.forEach((person) => vals.add(person.location));

  /* eslint-disable */
    return Array.from(vals).sort((a, b) => a.localeCompare(b));
  }, [jobs, talents]);

  const filteredJobs = useMemo(() => {
    const q = term.trim().toLowerCase();
    return jobs.filter((j) => {
      if (roleFilter && j.role !== roleFilter) return false;
      if (locationFilter && j.location !== locationFilter) return false;
      if (employmentFilter && j.type !== employmentFilter) return false;
      if (q && !(`${j.title} ${j.company} ${j.desc}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [jobs, term, roleFilter, locationFilter, employmentFilter]);
  const filteredTalents = useMemo(() => talents.filter((person) => {
    if (locationFilter && person.location !== locationFilter) return false;
    const q = term.trim().toLowerCase();
    return !q || `${person.name} ${person.role} ${person.skills || ""} ${person.bio || ""}`.toLowerCase().includes(q);
  }), [talents, term, locationFilter]);

  return (
    <div className="min-h-screen bg-[#0b1628] text-[#e2eaf5]">
      <header className="sticky top-0 z-20 border-b border-[#1a2a42] bg-[#0f1a2e]">
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between px-5 py-4">
          <Link href="/public" className="flex items-center gap-3">
            <span className="relative h-12 w-12 overflow-hidden rounded-xl border border-cyan-300/20 bg-cyan-300/10">
              <Image src="/brand/cybo-one-mark.png" alt="" fill loading="lazy" sizes="48px" className="object-contain p-1" />
            </span>
            <span>
              <span className="block font-['Montserrat'] text-xl font-bold text-white">Cybo <span className="text-[#3ad4ff]">One</span></span>
              <span className="-mt-1 block text-[11px] text-[#3ad4ff]">Job Portal</span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link href="/public/seeker-login" className="rounded-lg border border-[#3ad4ff]/50 px-3 py-2 text-sm font-semibold text-[#8be9ff] hover:bg-[#3ad4ff]/10">Sign In</Link>
            <Link href={"/public/seeker-register" as Route} className="rounded-lg bg-[#f4a623] px-3 py-2 text-sm font-bold text-[#0b1628] hover:brightness-105">Create Account</Link>
            <Link href="/public" className="hidden text-sm text-[#e2eaf5] hover:text-[#3ad4ff] sm:inline">← Home</Link>
          </div>
        </div>
      </header>

      <section className="border-b border-[#1a2a42] bg-gradient-to-br from-[#0b1628] via-[#1a2a42] to-[#0f1a2e] py-14">
        <div className="mx-auto w-full max-w-[1400px] px-5">
          <h1 className="font-['Montserrat'] text-4xl font-bold sm:text-5xl">
            Hire Hospitality <span className="text-[#f4a623]">Talent</span>
          </h1>
          <p className="mt-3 max-w-3xl text-lg text-[#a8b5cc]">
            Discover hospitality roles and opt-in professional profiles. Organizations can post jobs, review applications, and build teams across locations.
          </p>
        </div>
      </section>

      <section className="border-b border-[#1a2a42] bg-[#0f1a2e] py-7">
        <div className="mx-auto grid w-full max-w-[1400px] gap-3 px-5 md:grid-cols-5">
          <div className="relative md:col-span-2">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#a8b5cc]" />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              aria-label="Search jobs or talent"
              placeholder="Job title, company, or skills..."
              className="w-full rounded-md border border-[#1a2a42] bg-transparent py-2.5 pl-9 pr-3 text-sm text-[#e2eaf5] placeholder:text-[#a8b5cc] focus:border-[#f4a623] focus:outline-none"
            />
          </div>

          <div className="relative">
            <Briefcase size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#a8b5cc]" />
            <select
              aria-label="Job role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full appearance-none rounded-md border border-[#1a2a42] bg-[#0f1a2e] py-2.5 pl-9 pr-3 text-sm text-[#e2eaf5] focus:border-[#f4a623] focus:outline-none"
            >
              <option value="">All Roles</option>
              <option value="manager">Manager</option>
              <option value="chef">Chef</option>
              <option value="front-desk">Front Desk</option>
              <option value="housekeeper">Housekeeper</option>
              <option value="bartender">Bartender</option>
              <option value="other">Other hospitality roles</option>
            </select>
          </div>

          <div className="relative">
            <select aria-label="Employment type" value={employmentFilter} onChange={(e) => setEmploymentFilter(e.target.value)} className="w-full rounded-md border border-[#1a2a42] bg-[#0f1a2e] px-3 py-2.5 text-sm text-[#e2eaf5] focus:border-[#f4a623] focus:outline-none">
              <option value="">All Employment Types</option>
              {["Full-time", "Part-time", "Contract", "Temporary", "Internship", "Apprenticeship", "Seasonal", "Casual", "Volunteer", "Other"].map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </div>

          <div className="relative">
            <MapPin size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#a8b5cc]" />
            <select
              aria-label="Location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full appearance-none rounded-md border border-[#1a2a42] bg-[#0f1a2e] py-2.5 pl-9 pr-3 text-sm text-[#e2eaf5] focus:border-[#f4a623] focus:outline-none"
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <main className="mx-auto w-full max-w-[1400px] px-5 pb-8 pt-6">
        {loadError ? <p role="alert" className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">{loadError}<button onClick={() => setRetryVersion((value) => value + 1)} className="ml-4 font-bold underline">Try again</button></p> : null}
        <div className="mb-6 flex gap-4 border-b border-[#1a2a42]">
          <button
            type="button"
            onClick={() => setTab("jobs")}
            className={`border-b-2 px-3 py-3 text-sm font-semibold ${
              tab === "jobs" ? "border-[#f4a623] text-[#f4a623]" : "border-transparent text-[#a8b5cc] hover:text-[#e2eaf5]"
            }`}
          >
            <Briefcase size={14} className="mr-1 inline" /> Open Jobs
          </button>
          <button
            type="button"
            onClick={() => setTab("talents")}
            className={`border-b-2 px-3 py-3 text-sm font-semibold ${
              tab === "talents" ? "border-[#f4a623] text-[#f4a623]" : "border-transparent text-[#a8b5cc] hover:text-[#e2eaf5]"
            }`}
          >
            <Users size={14} className="mr-1 inline" /> Find Talent
          </button>
        </div>

        {tab === "jobs" ? (
          filteredJobs.length === 0 ? (
            <div className="rounded-xl border border-[#1a2a42] bg-[#0f1a2e] p-10 text-center">
              <p className="text-lg font-semibold text-[#e2eaf5]">{loadError ? "Jobs are temporarily unavailable." : jobs.length === 0 ? "No published jobs yet." : "No jobs found."}</p>
              <p className="mt-2 text-sm text-[#a8b5cc]">
                {loadError ? "Please try again shortly." : jobs.length === 0
                  ? "Open roles will appear here automatically when HR publishes them."
                  : "Try changing role, location, or search keyword."}
              </p>
            </div>
          ) : (
            <div className="space-y-5 pb-4">
              {filteredJobs.map((j) => (
                <article
                  key={j.id}
                  className="cursor-pointer rounded-xl border border-[#1a2a42] bg-[#0f1a2e] p-6 transition hover:translate-x-1 hover:border-[#f4a623]"
                >
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <h2 className="text-xl font-semibold text-[#e2eaf5]"><Link href={publicJobPath(j.title, j.id) as Route} className="hover:text-[#f4a623]">{j.title}</Link></h2>
                      <p className="text-sm text-[#a8b5cc]">{j.company}</p>
                    </div>
                    <span className="rounded-full bg-gradient-to-br from-[#f4a623] to-[#3ad4ff] px-3 py-1 text-xs font-semibold text-[#0b1628]">
                      {j.type}
                    </span>
                  </div>

                  <div className="mb-3 flex flex-wrap gap-5 text-sm text-[#a8b5cc]">
                    <p className="flex items-center gap-1">
                      <MapPin size={14} /> {j.location}
                    </p>
                    <p className="flex items-center gap-1">
                      <Briefcase size={14} /> Hospitality
                    </p>
                  </div>

                  <p className="mb-4 text-sm leading-relaxed text-[#a8b5cc]">{j.desc}</p>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-2xl font-bold text-[#2eff9e]">
                      {j.salary && j.salaryMax ? `$${j.salary}-$${j.salaryMax}/month` : "Salary not disclosed"}
                    </p>
                    <div className="flex gap-2">
                      {j.applicationMethod === "external" ? (
                        j.applicationUrl ? <a href={j.applicationUrl} target="_blank" rel="noopener noreferrer" className="rounded-md bg-[#f4a623] px-4 py-2 text-sm font-semibold text-[#0b1628] hover:brightness-105">Apply on employer site ↗</a> : <span className="text-sm text-amber-200">Employer application link unavailable</span>
                      ) : <button type="button" onClick={() => openSeekerFlow("apply", j.id)} className="rounded-md bg-[#f4a623] px-4 py-2 text-sm font-semibold text-[#0b1628] hover:brightness-105">Apply Now</button>}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openSeekerFlow("save", j.id);
                        }}
                        className="rounded-md border border-[#1a2a42] px-3 py-2 text-sm text-[#e2eaf5] hover:bg-[#0b1628]"
                        aria-label="Save job"
                      >
                        ★
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )
        ) : (
          filteredTalents.length === 0 ? (
            <div className="rounded-xl border border-[#1a2a42] bg-[#0f1a2e] p-10 text-center">
              <p className="text-lg font-semibold text-[#e2eaf5]">{talents.length ? "No profiles match this search." : "No public talent profiles yet."}</p>
              <p className="mt-2 text-sm text-[#a8b5cc]">Candidates appear here only after they choose to publish their profile.</p>
            </div>
          ) : (
            <div className="space-y-4 pb-4">
              {filteredTalents.map((t) => (
              <article key={t.id} className="rounded-xl border border-[#1a2a42] bg-[#0f1a2e] p-5">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">{t.photo_url ? <img src={t.photo_url} alt="" className="h-12 w-12 rounded-full object-cover" /> : <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-500/15 font-bold text-cyan-200">{t.name.slice(0,1)}</span>}<h3 className="text-lg font-semibold text-[#e2eaf5]">{t.name}</h3></div>
                  <span className="rounded-full bg-[#3ad4ff]/20 px-2.5 py-1 text-xs font-semibold text-[#3ad4ff]">{t.availability}</span>
                </div>
                <p className="text-sm text-[#a8b5cc]">{t.role}</p>
                {t.bio ? <p className="mt-3 line-clamp-3 text-sm text-[#a8b5cc]">{t.bio}</p> : null}
                {t.skills ? <div className="mt-3 flex flex-wrap gap-2">{t.skills.split(",").slice(0,8).map((skill) => <span key={skill} className="rounded-full border border-cyan-400/20 px-2 py-1 text-xs text-cyan-200">{skill.trim()}</span>)}</div> : null}
                <div className="mt-2 flex flex-wrap gap-4 text-sm text-[#a8b5cc]">
                  <p className="flex items-center gap-1">
                    <MapPin size={14} /> {t.location}
                  </p>
                  <p className="flex items-center gap-1">
                    <Briefcase size={14} /> {t.experience} experience
                  </p>
                </div>
                <p className="mt-4 text-xs text-[#8294ac]">Contact requests are managed through the organization recruitment workspace; personal contact details stay private.</p>
              </article>
              ))}
            </div>
          )
        )}
      </main>

      <footer className="border-t border-[#1a2a42] bg-[#0f1a2e] py-8">
        <div className="mx-auto flex w-full max-w-[1400px] flex-wrap items-center justify-between gap-3 px-5 text-sm text-[#a8b5cc]">
          <p>© 2026 Cybo One. Global hospitality network.</p>
          <div className="flex items-center gap-4">
            <Link href="/public" className="hover:text-[#3ad4ff]">
              Home
            </Link>
            <Link href="/public/partner-request" className="hover:text-[#3ad4ff]">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
